import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ShieldCheck, Lock, X, AlertTriangle } from "lucide-react";

const DISMISS_KEY = "fodindin:safety-tips-dismissed-at";
// Auto-resurfaces the legal disclaimer at least once per week so users
// can never "accidentally" opt out of it forever — important for the
// platform's liability shield.
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** Returns true if the user dismissed the modal within the last DISMISS_TTL_MS. */
function isDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

/**
 * Pre-contact safety advisory modal.
 *
 * Wrap any phone/WhatsApp button with <SafetyGate href="...">. The first time
 * the user clicks, the modal appears with security tips + a legal disclaimer.
 * Only after the user clicks "OK" does the original href fire (tel:, wa.me, ...).
 * Users can opt out via "Ne plus afficher" — preference is stored in localStorage.
 */
export function SafetyGate({ children, onProceed, dataTestid }) {
  const [open, setOpen] = useState(false);
  const [dontShow, setDontShow] = useState(false);

  const handleClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDismissed()) {
      onProceed?.();
      return;
    }
    setDontShow(false);
    setOpen(true);
  }, [onProceed]);

  const handleConfirm = () => {
    if (dontShow) {
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore quota errors */ }
    }
    setOpen(false);
    onProceed?.();
  };

  return (
    <>
      <span onClickCapture={handleClick} data-testid={dataTestid} className="contents">
        {children}
      </span>
      {open && (
        <SafetyTipsModalUI
          onClose={() => setOpen(false)}
          onConfirm={handleConfirm}
          dontShow={dontShow}
          setDontShow={setDontShow}
        />
      )}
    </>
  );
}

function SafetyTipsModalUI({ onClose, onConfirm, dontShow, setDontShow }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
      style={{ zIndex: 10000 }}
      onClick={onClose}
      data-testid="safety-tips-modal"
    >
      <div
        className="bg-white rounded-3xl max-w-lg w-full shadow-2xl my-auto overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header illustration band */}
        <div className="relative bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 px-8 pt-8 pb-6">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-[var(--ink-soft)] hover:text-[var(--ink)] p-1.5 rounded-full hover:bg-white/60"
            data-testid="safety-tips-close"
            aria-label="Fermer"
            type="button"
          >
            <X size={18} />
          </button>
          <div className="flex justify-center">
            <div className="relative">
              <div className="w-20 h-20 rounded-full bg-white grid place-items-center shadow-lg">
                <Lock size={32} className="text-orange-600" />
              </div>
              <div className="absolute -top-1 -right-2 w-8 h-8 rounded-full bg-amber-400 grid place-items-center shadow">
                <ShieldCheck size={14} className="text-white" />
              </div>
            </div>
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-center text-orange-500 mt-5">
            Conseils de sécurité
          </h2>
        </div>

        {/* Body */}
        <div className="px-8 py-6 space-y-4 text-[15px] leading-relaxed text-[var(--ink)]">
          <Tip n={1}>
            <strong>Ne payez jamais sans avoir vérifié le produit</strong> en personne et constaté
            son état réel.
          </Tip>
          <Tip n={2}>
            <strong>N&apos;envoyez pas d&apos;argent par Mobile Money, virement bancaire, mandat
            ou tout autre moyen de transfert à distance</strong> avant la remise du bien.
          </Tip>
          <Tip n={3}>
            <strong>Donnez rendez-vous au vendeur dans un lieu public</strong> et passant, à une
            heure de forte affluence. Évitez les rencontres isolées.
          </Tip>
          <Tip n={4}>
            <strong>Méfiez-vous des prix anormalement bas</strong> et des vendeurs pressés. Vérifiez
            les documents (titre foncier, CNI, facture) avant tout engagement.
          </Tip>

          <div className="mt-4 p-3 rounded-2xl bg-red-50 border border-red-100 flex items-start gap-2.5">
            <AlertTriangle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-red-700 leading-snug">
              <strong>Avertissement légal :</strong> Fodindin.com est un service de mise en relation.
              Le site n&apos;est <strong>pas partie aux transactions</strong> conclues entre acheteurs et
              vendeurs et <strong>décline toute responsabilité</strong> en cas de litige, fraude ou
              préjudice résultant d&apos;échanges initiés via la plateforme. L&apos;utilisateur reconnaît
              avoir pris connaissance de ces conseils et agir sous sa seule responsabilité.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 pb-7 pt-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="safety-tips-dont-show-label">
            <input
              type="checkbox"
              checked={dontShow}
              onChange={(e) => setDontShow(e.target.checked)}
              className="w-4 h-4 rounded border-2 border-[var(--line)] accent-orange-600"
              data-testid="safety-tips-dont-show"
            />
            <span className="text-[var(--ink-soft)]">Ne plus afficher pendant 7 jours.</span>
          </label>
          <button
            type="button"
            onClick={onConfirm}
            className="bg-orange-600 hover:bg-orange-700 text-white font-bold px-8 py-3 rounded-full shadow-md transition inline-flex items-center justify-center gap-2"
            data-testid="safety-tips-ok-btn"
          >
            OK, j&apos;ai compris
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Tip({ n, children }) {
  return (
    <div className="flex gap-3 items-start" data-testid={`safety-tip-${n}`}>
      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-orange-100 text-orange-700 font-extrabold text-sm grid place-items-center">
        {n}
      </span>
      <p className="flex-1 text-[14px] leading-snug">{children}</p>
    </div>
  );
}
